import { useState } from 'react';

import api from '@/app/services/axios.service';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { OnboardingData, OnboardingStep } from '../index';

interface SignupFormProps {
  onNext: (data: Partial<OnboardingData>, nextStep?: OnboardingStep) => void;
  data: OnboardingData;
}

export default function SignupForm({ onNext, data }: SignupFormProps) {
  const [email, setEmail] = useState(data.email || '');
  const [error, setError] = useState<string | string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.post('/users/verify-email', {
        email,
        inviteCode: data.inviteCode,
      });
      if (response.status < 200 || response.status >= 300) {
        setError(response.data?.message || 'Failed to verify email');
        setIsLoading(false);
        return;
      }
      onNext({ email });
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;

      // Format error messages to be more user-friendly
      const formatErrorMessage = (msg: string) => {
        if (msg === 'email must be an email' || msg === 'email should not be empty') {
          return 'Please enter a valid email';
        }
        return msg;
      };

      if (Array.isArray(backendMsg)) {
        // Format each message in the array
        setError(backendMsg.map(formatErrorMessage));
      } else {
        setError(formatErrorMessage(backendMsg) || 'Failed to verify email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto space-y-8">
      <div className="flex flex-col items-center gap-2">
        <img src="/icons/brand/revi/monogram-black.svg" alt="revi-logo" className="mb-4 size-8" />
        <h1 className="text-3xl text-center">Welcome to Revi</h1>
        <p className="text-sm font-normal text-muted-foreground">{data.company}</p>
      </div>

      <div className="w-full space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={`your.email@${data.domain || ''}`}
            className={cn({ 'bg-destructive/10 text-destructive border-destructive': error })}
          />
        </div>{' '}
        {error && (
          <Alert variant="destructive" className="p-0 pl-2 -mt-2 border-none">
            <AlertDescription className="p-0 text-xs font-normal">
              {Array.isArray(error) ? error.map((msg, i) => <div key={i}>{msg}</div>) : error}
            </AlertDescription>
          </Alert>
        )}
        <Button
          onClick={handleSubmit}
          className="w-full"
          variant="default"
          size="lg"
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Continue'}
        </Button>
        <p className="text-sm font-normal text-center text-muted-foreground">
          By clicking, you agree to our{' '}
          <a href="https://www.revi.ai/terms-of-service" className="underline" target="_blank">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://www.revi.ai/privacy-policy" className="underline" target="_blank">
            Privacy Policy
          </a>
          .
        </p>
        <p className="flex items-center justify-center gap-2 text-sm font-normal text-center text-muted-foreground">
          Already have an account?{' '}
          <a href="/" className="text-foreground hover:text-foreground/80 hover:underline">
            Login
          </a>
        </p>
      </div>
    </div>
  );
}
